import React from 'react'
import {
  AppBar,
  Box,
  Button,
  Container,
  Stack,
  Toolbar,
  Typography
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import ArrowForward from '@mui/icons-material/ArrowForward'
import Spacer from '@hypotenuse/common/src/components/atoms/Spacer'
import { useIsMobile } from '../../lib/hooks'
import { toMaterialShade } from '@hypotenuse/common/src/utils/ColorUtils'
import { IArticlePreview } from '@hypotenuse/common/src/interfaces/Blog'
import { DummyEditor } from '../editor/DummyEditor'

const InfoChip = styled(Box)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: toMaterialShade(theme.palette.primary.main, 50),
  borderRadius: theme.shape.borderRadius
}))

/**
 * URL query parameters for campaign tracking.
 * Note that these should only be present in production, not
 *  in development or staging.
 *
 */
const UTM_PARAMS = new URLSearchParams({
  utm_source: 'blogshare',
  utm_medium: 'banner',
  utm_campaign: 'blog_linkshare'
})

/**
 * URL To redirect to when the user clicks a call-to-action button.
 */
const CALL_TO_ACTION_URL = `https://www.hypotenuse.ai/ai-article-writer?${UTM_PARAMS}`
const LANDING_PAGE_URL = `https://www.hypotenuse.ai?${UTM_PARAMS}`

/**
 * Text transform applied to HTML article content.
 * Currently used to remove <p>&nbsp;</p> tags
 */
const _transformHtmlContent = (content: string, title: string): string => {
  const strippedContent = content.replace(/<p>&nbsp;<\/p>/g, '\n')
  return `<h1>${title}</h1>${strippedContent}`
}

interface ArticlePreviewProps {
  articlePreview: IArticlePreview
  logo: React.ReactNode
}

/**
 * Static mockup of the publicly viewable article preview page.
 * May change to include dynamic components in the future.
 */
const ArticlePreview: React.FC<ArticlePreviewProps> = (props) => {
  const { articlePreview, logo: _logoProp } = props
  const theme = useTheme()
  const isMobile = useIsMobile()

  const {
    html_content: articleHtmlContent,
    author_display_name: authorName,
    title
  } = articlePreview ?? {}

  // ==== Appbar contents ====
  const logo = <a href={LANDING_PAGE_URL}>{_logoProp}</a>
  /**
   * CTA button displayed at the very top of the page
   */
  const navbarCtaButton = (
    <Button
      component="a"
      href={CALL_TO_ACTION_URL}
      target="_blank"
      rel="noopener noreferrer"
      color="primary"
      variant="contained"
      endIcon={<ArrowForward />}
    >
      Try it yourself
    </Button>
  )
  const mobileAppBarContent = (
    // Logo in appbar should be centered on mobile
    <Stack flex={1} direction="row" justifyContent="center">
      {logo}
    </Stack>
  )
  const desktopAppBarContent = (
    <>
      {logo}
      <Spacer />
      {navbarCtaButton}
    </>
  )
  const appBarContent = isMobile ? mobileAppBarContent : desktopAppBarContent

  // ==== Author info ====
  const displayAuthorChip = authorName !== "an anonymous user"
  const authorChipText = `Published by ${authorName}`
  const mobileAuthorChip = (
    <Stack
      px={1}
      py={0.5}
      borderRadius={1}
      borderColor={theme.palette.primary.main}
    >
      <Typography component="div" align="center" color="primary">
        <Box fontWeight={700} fontSize="0.875rem">
          {authorChipText}
        </Box>
      </Typography>
    </Stack>
  )
  const desktopAuthorChip = (
    <Stack
      px={1}
      py={0.5}
      borderRadius={1}
      bgcolor={theme.palette.primary.main}
    >
      <Typography component="div" align="center">
        <Box
          color={theme.palette.getContrastText(theme.palette.primary.main)}
          fontWeight={500}
          fontSize="0.875rem"
        >
          {authorChipText}
        </Box>
      </Typography>
    </Stack>
  )
  const authorChip = displayAuthorChip ? (isMobile ? mobileAuthorChip : desktopAuthorChip) : null
  const infoChipText = 'This article was written and edited with AI.'
  const mobileInfoChip = (
    <InfoChip>
      <Stack spacing={1} p={1}>
        {authorChip}
        <Typography align="center">{infoChipText}</Typography>
      </Stack>
    </InfoChip>
  )
  const desktopInfoChip = (
    <InfoChip>
      {/*More padding on the right*/}
      <Stack
          direction="row"
          p={displayAuthorChip ? undefined : 1}
          pr={1}
          spacing={1}
          alignItems="center"
      >
        {authorChip}
        <Typography>
          <strong>{infoChipText}</strong>
        </Typography>
      </Stack>
    </InfoChip>
  )
  const infoChip = isMobile ? mobileInfoChip : desktopInfoChip

  // ==== CTA button ====
  /**
   * Call-to-action button displayed in banner above the article content
   */
  const mobileBannerCtaButton = (
    <Button
      component="a"
      href={CALL_TO_ACTION_URL}
      target="_blank"
      rel="noopener noreferrer"
      variant="contained"
      color="primary"
      endIcon={<ArrowForward />}
    >
      Try it yourself
    </Button>
  )
  const desktopBannerCtaButton = (
    <Button
      component="a"
      href={CALL_TO_ACTION_URL}
      target="_blank"
      rel="noopener noreferrer"
      variant="text"
      color="primary"
      endIcon={<ArrowForward />}
    >
      Learn more
    </Button>
  )
  const bannerCtaButton = isMobile
    ? mobileBannerCtaButton
    : desktopBannerCtaButton

  // ==== Banner ====
  const mobileBanner = (
    <Stack sx={{ color: theme.palette.primary.main }} width="100%" spacing={1}>
      {infoChip}
      {bannerCtaButton}
    </Stack>
  )
  const desktopBanner = (
    <Stack
      sx={{ color: theme.palette.primary.main }}
      direction="row"
      alignItems="center"
      width="100%"
      px={2}
      py={1}
      borderRadius={1}
      border={1}
    >
      {infoChip}
      <Spacer />
      {bannerCtaButton}
    </Stack>
  )
  const banner = isMobile ? mobileBanner : desktopBanner
  const editor = (
    <DummyEditor
      htmlContent={_transformHtmlContent(articleHtmlContent ?? '', title ?? '')}
    />
  )

  return (
    <>
      <AppBar sx={{ backgroundColor: theme.palette.grey[900] }}>
        <Toolbar>{appBarContent}</Toolbar>
      </AppBar>
      <Toolbar />
      <Container maxWidth="md" style={{ height: '100%' }}>
        <Stack pt={{ xs: 3, md: 6 }} pb={3} spacing={2} alignItems="center">
          {banner}
          {editor}
        </Stack>
      </Container>
    </>
  )
}
export default ArticlePreview
